import api from './api';
import { Template, TemplateListResponse } from '../types/template';
import {
  AdminUser,
  AdminUsersListResponse,
  DeadLetterListResponse,
  DeadLetterStats,
  ReplayResponse,
} from '../types/admin';

export type SubmissionStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export const adminService = {
  // ── Marketplace moderation ───────────────────────────────────

  /** List submitted templates for moderation (defaults to PENDING). */
  async listSubmissions(
    status: SubmissionStatus = 'PENDING',
    page = 1,
    limit = 20
  ): Promise<TemplateListResponse> {
    const response = await api.get<TemplateListResponse>(
      '/templates/admin/submissions',
      { params: { status, page, limit } }
    );
    return response.data;
  },

  async approveTemplate(templateId: string): Promise<Template> {
    const response = await api.post<Template>(
      `/templates/${templateId}/approve`
    );
    return response.data;
  },

  async rejectTemplate(templateId: string, reason: string): Promise<Template> {
    const response = await api.post<Template>(
      `/templates/${templateId}/reject`,
      { reason }
    );
    return response.data;
  },

  // ── Dead-letter queue ────────────────────────────────────────

  async listDeadLetters(page = 1, limit = 20): Promise<DeadLetterListResponse> {
    const response = await api.get<DeadLetterListResponse>(
      '/dead-letter-queue',
      { params: { page, limit } }
    );
    return response.data;
  },

  async deadLetterStats(): Promise<DeadLetterStats> {
    const response = await api.get<DeadLetterStats>('/dead-letter-queue/stats');
    return response.data;
  },

  async replayDeadLetter(entryId: string): Promise<ReplayResponse> {
    const response = await api.post<ReplayResponse>(
      `/dead-letter-queue/${entryId}/replay`
    );
    return response.data;
  },

  async discardDeadLetter(entryId: string): Promise<void> {
    await api.post(`/dead-letter-queue/${entryId}/discard`);
  },

  // ── User management ──────────────────────────────────────────

  async listUsers(
    search = '',
    page = 1,
    limit = 25
  ): Promise<AdminUsersListResponse> {
    const response = await api.get<AdminUsersListResponse>('/admin/users', {
      params: { search: search || undefined, page, limit },
    });
    return response.data;
  },

  async updateUser(
    userId: string,
    data: { role?: string; status?: string; isComped?: boolean }
  ): Promise<AdminUser> {
    const response = await api.patch<AdminUser>(`/admin/users/${userId}`, data);
    return response.data;
  },

  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/admin/users/${userId}`);
  },

  // ── Platform costs ───────────────────────────────────────────

  /** Platform-wide LLM spend — every user, i.e. our own bill. */
  async platformCosts(days = 30): Promise<AdminCosts> {
    const response = await api.get<AdminCosts>('/admin/costs', {
      params: { days },
    });
    return response.data;
  },
};

/** One user's spend. `hasOwnKey` users pay their own tokens. */
export interface AdminUserSpend {
  userId: string;
  email: string;
  isPro: boolean;
  hasOwnKey: boolean;
  runs: number;
  tokens: number;
  /** Fractional cents — a typical run is ~0.1c. */
  costCents: number;
}

export interface AdminDailyCost {
  date: string;
  runs: number;
  costCents: number;
}

export interface AdminCosts {
  days: number;
  totalCostCents: number;
  totalTokens: number;
  totalRuns: number;
  /** Spend by users without their own key — the part we actually pay. */
  billableCostCents: number;
  byoKeyUsers: number;
  projectedMonthlyCostCents: number;
  daily: AdminDailyCost[];
  topSpenders: AdminUserSpend[];
}
